import { useState, useEffect, useRef } from 'react';
import API from '../utils/API';
import { LEADERSHIP_TITLES_BY_ORG } from '../utils/leadershipTitles';
import './joblist.css';

const CUSTOM_TITLE = '__custom__';

export default function AddLeadershipPositionModal(props) {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileSearch, setProfileSearch] = useState('');
  const [profileResults, setProfileResults] = useState([]);
  const [profileSearching, setProfileSearching] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [highlightedProfileIndex, setHighlightedProfileIndex] = useState(-1);
  const profileRef = useRef(null);

  const [titleSelect, setTitleSelect] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const titleOptions = LEADERSHIP_TITLES_BY_ORG[props.organization_id] || [];
  const title = titleSelect === CUSTOM_TITLE ? customTitle : titleSelect;

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced profile search
  useEffect(() => {
    if (profileSearch.length < 2 || selectedProfile) {
      setProfileResults([]);
      setShowProfileDropdown(false);
      return;
    }
    const t = setTimeout(() => {
      setProfileSearching(true);
      setHighlightedProfileIndex(-1);
      API.searchProfilesForContact(profileSearch)
        .then(r => { setProfileResults(r.data); setShowProfileDropdown(r.data.length > 0); })
        .catch(() => {})
        .finally(() => setProfileSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [profileSearch, selectedProfile]);

  const profileSecondaryText = (profile) => {
    const job = profile.JobExperiences?.[0];
    if (!job) return null;
    return `${job.title} — ${job.Organization?.organization_name || ''}`;
  };

  const selectProfile = (profile) => {
    setSelectedProfile(profile);
    setProfileSearch(`${profile.first_name} ${profile.last_name}`);
    setShowProfileDropdown(false);
    setProfileResults([]);
  };

  const clearProfile = () => {
    setSelectedProfile(null);
    setProfileSearch('');
    setProfileResults([]);
    setShowProfileDropdown(false);
    setHighlightedProfileIndex(-1);
  };

  const handleProfileKeyDown = (e) => {
    if (!showProfileDropdown || profileResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedProfileIndex(i => Math.min(i + 1, profileResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedProfileIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedProfileIndex >= 0) {
      e.preventDefault();
      selectProfile(profileResults[highlightedProfileIndex]);
      setHighlightedProfileIndex(-1);
    } else if (e.key === 'Escape') {
      setShowProfileDropdown(false);
      setHighlightedProfileIndex(-1);
    }
  };

  const isValid = !!(selectedProfile && title && startDate);

  const clearForm = () => {
    clearProfile();
    setTitleSelect('');
    setCustomTitle('');
    setStartDate('');
    setEndDate('');
  };

  const handleSubmit = async () => {
    try {
      const data = {
        organization_id: props.organization_id,
        profile_id: selectedProfile.profile_id,
        title,
        start_date: startDate,
      };
      if (endDate) data.end_date = endDate;

      await API.addLeadershipPosition(data);
      props.reRenderLeadership();
      clearForm();
    } catch (err) {
      console.error('Add leadership position failed:', err);
    }
  };

  return (
    <div className="modal fade" id="addLeadershipModal" data-bs-backdrop="static" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-scrollable">
        <div className="modal-content">

          <div className="jm-modal-header">
            <h5 className="jm-modal-title"><i className="bi bi-person-badge me-2" />Add Leadership Position</h5>
          </div>

          <div className="modal-body jm-body">

            <div className="jm-field">
              <label className="jm-label">Person <span className="jm-req">*</span></label>
              <div className="jm-typeahead" ref={profileRef}>
                <div className="jm-typeahead-wrap">
                  <input
                    className="jm-input"
                    placeholder="Search people…"
                    value={profileSearch}
                    onChange={e => { setProfileSearch(e.target.value); if (selectedProfile) clearProfile(); }}
                    onFocus={() => profileResults.length > 0 && setShowProfileDropdown(true)}
                    onKeyDown={handleProfileKeyDown}
                    autoComplete="off"
                  />
                  {profileSearching && <span className="jm-spinner" />}
                  {selectedProfile && (
                    <button type="button" className="jm-typeahead-clear" onClick={clearProfile} title="Clear">
                      <i className="bi bi-x-lg" />
                    </button>
                  )}
                </div>
                {showProfileDropdown && profileResults.length > 0 && (
                  <div className="jm-typeahead-dropdown">
                    {profileResults.map((profile, idx) => (
                      <button
                        type="button"
                        key={profile.profile_id}
                        className={`jm-typeahead-item${idx === highlightedProfileIndex ? ' jm-typeahead-item--highlighted' : ''}`}
                        onMouseDown={() => selectProfile(profile)}
                        onMouseEnter={() => setHighlightedProfileIndex(idx)}
                      >
                        <span className="jm-typeahead-name">{profile.first_name} {profile.last_name}</span>
                        {profileSecondaryText(profile) && <span className="jm-typeahead-meta">{profileSecondaryText(profile)}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="jm-field">
              <label className="jm-label">Title <span className="jm-req">*</span></label>
              <select className="jm-select" value={titleSelect} onChange={e => setTitleSelect(e.target.value)}>
                <option value="" disabled>Select title…</option>
                {titleOptions.map(t => <option key={t} value={t}>{t}</option>)}
                <option value={CUSTOM_TITLE}>Other / custom title…</option>
              </select>
            </div>

            {titleSelect === CUSTOM_TITLE && (
              <div className="jm-field">
                <label className="jm-label">Custom Title <span className="jm-req">*</span></label>
                <input className="jm-input" value={customTitle} onChange={e => setCustomTitle(e.target.value)} />
              </div>
            )}

            <div className="jm-grid-2">
              <div className="jm-field">
                <label className="jm-label">Start Date <span className="jm-req">*</span></label>
                <input className="jm-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="jm-field">
                <label className="jm-label">End Date <span className="jm-opt">blank = current</span></label>
                <input className="jm-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>

          </div>

          <div className="jm-modal-footer">
            <button type="button" className="jm-btn-ghost" data-bs-dismiss="modal" onClick={clearForm}>
              Close
            </button>
            <button
              type="button"
              className="jm-btn-primary"
              data-bs-dismiss="modal"
              onClick={handleSubmit}
              disabled={!isValid}
            >
              Add
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
